import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import type { CriteriaWithLabel } from './types';
import { CriteriaBuilder, type CriteriaCondition } from '../CriteriaBuilder';

interface Grid2DAxisConfigProps {
  axis: 'x' | 'y';
  axisLabel: string;
  entityType: string;
  criteria: CriteriaWithLabel[];
  onChange: (criteria: CriteriaWithLabel[]) => void;
  minSize?: number;
  maxSize?: number;
  disabled?: boolean;
}

const AXIS_COLORS = {
  x: '#e3f2fd', // blue
  y: '#f3e5f5', // purple
};

const AXIS_DESCRIPTIONS = {
  x: 'Row criteria (displayed on left side of grid)',
  y: 'Column criteria (displayed on top of grid)',
};

const createEmptyCriteria = (): CriteriaWithLabel => ({
  label: '',
  dsl: '',
  conditions: [],
});

export const Grid2DAxisConfig = ({
  axis,
  axisLabel,
  entityType,
  criteria,
  onChange,
  minSize = 2,
  maxSize = 6,
  disabled = false,
}: Grid2DAxisConfigProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | false>(0);

  const handleLabelChange = (index: number, label: string) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], label };
    onChange(updated);
  };

  const handleConditionsChange = (index: number, conditions: CriteriaCondition[]) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], conditions };
    onChange(updated);
  };

  const handleDslChange = (index: number, dsl: string) => {
    // Only update if DSL actually changed to avoid clearing validation
    if (criteria[index]?.dsl === dsl) return;
    const updated = [...criteria];
    updated[index] = { ...updated[index], dsl };
    onChange(updated);
  };

  const handleAdd = () => {
    if (criteria.length < maxSize) {
      const updated = [...criteria, createEmptyCriteria()];
      onChange(updated);
      setExpandedIndex(updated.length - 1);
    }
  };

  const handleRemove = (index: number) => {
    if (criteria.length > minSize) {
      const updated = criteria.filter((_, i) => i !== index);
      onChange(updated);
      if (expandedIndex === index) {
        setExpandedIndex(false);
      } else if (typeof expandedIndex === 'number' && expandedIndex > index) {
        setExpandedIndex(expandedIndex - 1);
      }
    }
  };

  return (
    <Card sx={{ backgroundColor: AXIS_COLORS[axis], mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {axisLabel} Axis
          </Typography>
          <Chip
            label={`${criteria.length} criteria`}
            size="small"
            color={criteria.every((c) => c.conditions.length > 0) ? 'success' : 'default'}
          />
          <Tooltip title="Add criterion">
            <span>
              <IconButton
                size="small"
                onClick={handleAdd}
                disabled={disabled || criteria.length >= maxSize}
                color="primary"
              >
                <AddCircleIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {AXIS_DESCRIPTIONS[axis]}
        </Typography>

        {criteria.map((criterion, index) => (
          <Accordion
            key={index}
            expanded={expandedIndex === index}
            onChange={(_, isExpanded) => setExpandedIndex(isExpanded ? index : false)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Chip
                  label={`${axis.toUpperCase()}${index + 1}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Typography sx={{ flex: 1 }}>
                  {criterion.label || `Criterion ${index + 1}`}
                </Typography>
                {criterion.conditions.length > 0 && (
                  <Chip
                    label={`${criterion.conditions.length} condition${criterion.conditions.length > 1 ? 's' : ''}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
                {criteria.length > minSize && (
                  <Tooltip title="Remove criterion">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(index);
                      }}
                      disabled={disabled}
                      color="error"
                    >
                      <RemoveCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="Label"
                value={criterion.label}
                onChange={(e) => handleLabelChange(index, e.target.value)}
                fullWidth
                size="small"
                disabled={disabled}
                sx={{ mb: 2 }}
                helperText="Display label for this criterion (shown in game UI)"
              />
              <CriteriaBuilder
                entityType={entityType}
                value={criterion.conditions}
                onChange={(conditions) => handleConditionsChange(index, conditions)}
                onDslChange={(dsl) => handleDslChange(index, dsl)}
                disabled={disabled}
                label="Conditions"
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
};
